import measurementDropIconUrl from "@/assets/measurement-drop.svg"
import BackButton from "@/components/BackButton"
import { Button } from "@/components/chadcn/Button"
import { Label } from "@/components/chadcn/Label"
import { SelectInput, TextareaInput } from "@/components/InputFields"
import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { useSelfQuery } from "@/data/queries"
import {
  useCreateBloodGlucoseFormMutation,
  useObservationByIdQuery,
  useUpdateBloodGlucoseFormMutation,
} from "@/data/queries/measurementQueries"
import { medicationTakenAnswers, toMedicationLog, type BloodGlucoseForm } from "@/model/BloodGlucoseForm"
import type { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation"
import {
  hasPrescribedMedicationForContext,
  medicationQuestionTitle,
  type PrescribedDiabetesMedication,
} from "@/model/Medication"
import { ROUTES, buildPath } from "@/routes"
import cn from "@/util/cn"
import {
  fastingMeasurementSchema,
  mealMeasurementSchema,
  type FastingMeasurementFormData,
  type MealContext,
  type MealMeasurementFormData,
} from "@/schemas/measurements"
import { zodResolver } from "@hookform/resolvers/zod"
import { DateTimeFormatter } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { useEffect, useMemo, useState } from "react"
import { Controller, useForm, useWatch, type DefaultValues } from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router"
import { toast } from "sonner"
import MealLog from "./components/MealLog"
import NotesField from "./components/NotesField"

const DAY_HEADING = DateTimeFormatter.ofPattern("EEEE, MMM dd").withLocale(Locale.US)
const TIME_FORMAT = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US)

const ReadingSummary = ({ observation }: { observation: BloodGlucoseObservation }) => {
  const reading = observation.reading?.bloodGlucoseInMilligramsPerDeciliter
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-gray-200 px-4 py-3">
      <div className="flex flex-col">
        <span className="text-base font-bold text-primary-contrast">
          {observation.dateTime.format(DAY_HEADING)}
        </span>
        <span className="text-sm font-medium text-primary-contrast/80">
          {observation.dateTime.format(TIME_FORMAT)}
        </span>
      </div>
      <div className="flex flex-col items-end">
        <div className="flex items-center gap-1 text-sm text-primary-contrast">
          <img alt="" src={measurementDropIconUrl} className="h-4 w-4" />
          <span>Glucose</span>
        </div>
        {reading !== undefined && (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-primary-contrast">{reading}</span>
            <span className="text-sm text-primary-contrast">mg/dL</span>
          </div>
        )}
      </div>
    </div>
  )
}

const mealContextOptions: { value: MealContext; title: string }[] = [
  { value: "breakfast", title: "Breakfast" },
  { value: "lunch", title: "Lunch" },
  { value: "dinner", title: "Dinner" },
  { value: "snack-before-breakfast", title: "Snack before breakfast" },
  {
    value: "snack-between-breakfast-and-lunch",
    title: "Snack between breakfast and lunch",
  },
  {
    value: "snack-between-lunch-and-dinner",
    title: "Snack between lunch and dinner",
  },
  { value: "snack-after-dinner", title: "Snack after dinner" },
]

const isMealContext = (context: string): context is MealContext =>
  mealContextOptions.some((option) => option.value === context)

const medicationOptions = medicationTakenAnswers.map((value) => ({
  value,
  label: value === "YES" ? "Yes" : "No",
}))

const useDashboardReturnUrl = (fallbackTab = "overview") => {
  const location = useLocation()
  const state = location.state as { initialTab?: string } | null
  return `${ROUTES.DASHBOARD}?tab=${state?.initialTab ?? fallbackTab}`
}

type FastingFormValues = FastingMeasurementFormData
type MealFormValues = MealMeasurementFormData

type FastingPanelProps = {
  observation: BloodGlucoseObservation
  initialValues: DefaultValues<FastingFormValues>
  isEditable: boolean
  isPending: boolean
  title: string
  onSubmit: (data: FastingFormValues) => Promise<void>
}

const FastingFormPanel = ({
  observation,
  initialValues,
  isEditable,
  isPending,
  title,
  onSubmit,
}: FastingPanelProps) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FastingFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(fastingMeasurementSchema),
    defaultValues: initialValues,
  })

  useEffect(() => {
    reset(initialValues)
  }, [reset, initialValues])

  return (
    <div className="flex flex-col gap-6">
      <BackButton />
      <ReadingSummary observation={observation} />

      {!isEditable && (
        <p className="rounded-lg bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          This reading can no longer be edited.
        </p>
      )}

      <form
        id="edit-fasting-measurement-form"
        className="flex flex-col gap-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2 className="text-base font-semibold text-primary-contrast">{title}</h2>
        <NotesField
          disabled={isPending || !isEditable}
          isError={!!errors.notes}
          {...register("notes")}
        />
        {errors.notes && <p className="-mt-4 text-sm text-error">{errors.notes.message}</p>}
      </form>

      <Button
        type="submit"
        form="edit-fasting-measurement-form"
        disabled={isPending || !isEditable}
        className="flex w-full gap-3 py-2 text-base"
      >
        {isPending && <Spinner />}
        Save reading
      </Button>
    </div>
  )
}

type MealPanelProps = {
  observation: BloodGlucoseObservation
  initialValues: DefaultValues<MealFormValues>
  isEditable: boolean
  isPending: boolean
  prescribedDiabetesMedications: PrescribedDiabetesMedication[]
  onSubmit: (data: MealFormValues) => Promise<void>
}

const MealFormPanel = ({
  observation,
  initialValues,
  isEditable,
  isPending,
  prescribedDiabetesMedications,
  onSubmit,
}: MealPanelProps) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors },
  } = useForm<MealFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(mealMeasurementSchema),
    defaultValues: initialValues,
  })

  useEffect(() => {
    reset(initialValues)
  }, [reset, initialValues])

  const context = useWatch({ control, name: "context" })
  const showMedicationQuestion =
    context !== undefined &&
    hasPrescribedMedicationForContext(prescribedDiabetesMedications, context)

  // The medication answer belongs to the meal context it was logged against.
  // Switching to a different context clears the answer and note; returning to
  // the originally-saved context restores them. Nothing persists until save.
  useEffect(() => {
    if (context === initialValues.context) {
      setValue("medicationTaken", initialValues.medicationTaken ?? null)
      setValue("medicationNote", initialValues.medicationNote ?? "")
    } else {
      setValue("medicationTaken", null)
      setValue("medicationNote", "")
    }
  }, [context, initialValues, setValue])

  return (
    <div className="flex flex-col gap-6">
      <BackButton />
      <ReadingSummary observation={observation} />

      {!isEditable && (
        <p className="rounded-lg bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          This reading can no longer be edited.
        </p>
      )}

      <form
        id="edit-meal-measurement-form"
        className="flex flex-col gap-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-1">
          <Label htmlFor="context">Meal context</Label>
          <Controller
            name="context"
            control={control}
            render={({ field }) => (
              <SelectInput
                id="context"
                disabled={isPending || !isEditable}
                value={field.value}
                options={mealContextOptions}
                onChange={(e) => field.onChange(e.target.value as MealContext)}
              />
            )}
          />
          {errors.context && <p className="text-sm text-error">{errors.context.message}</p>}
        </div>

        {showMedicationQuestion && (
          <div className="flex flex-col gap-3 rounded-lg bg-gray-100 p-4">
            <Label>{medicationQuestionTitle}</Label>
            <Controller
              name="medicationTaken"
              control={control}
              render={({ field }) => (
                <div role="radiogroup" className="flex flex-col gap-3">
                  {medicationOptions.map(({ value, label }) => {
                    const selected = field.value === value
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        disabled={isPending || !isEditable}
                        onClick={() => field.onChange(value)}
                        className="flex items-center gap-3 text-base text-primary-contrast disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span
                          className={cn(
                            "flex size-5 shrink-0 items-center justify-center rounded-full border-2",
                            selected ? "border-primary" : "border-primary/40"
                          )}
                        >
                          {selected && <span className="size-2.5 rounded-full bg-primary" />}
                        </span>
                        {label}
                      </button>
                    )
                  })}
                </div>
              )}
            />

            <div className="flex flex-col gap-1">
              <Label htmlFor="medicationNote">Medication note (optional)</Label>
              <TextareaInput
                id="medicationNote"
                rows={2}
                placeholder="e.g. I took it about an hour late"
                disabled={isPending || !isEditable}
                {...register("medicationNote")}
              />
            </div>
          </div>
        )}

        <div className="rounded-lg bg-gray-100 p-4">
          <MealLog register={register} errors={errors} disabled={isPending || !isEditable} />
        </div>

        <NotesField
          disabled={isPending || !isEditable}
          isError={!!errors.notes}
          {...register("notes")}
        />
        {errors.notes && <p className="-mt-4 text-sm text-error">{errors.notes.message}</p>}
      </form>

      <Button
        type="submit"
        form="edit-meal-measurement-form"
        disabled={isPending || !isEditable}
        className="flex w-full gap-3 py-2 text-base"
      >
        {isPending && <Spinner />}
        Save reading
      </Button>
    </div>
  )
}

const FastingEditForm = ({
  observation,
  form,
  patientId,
}: {
  observation: BloodGlucoseObservation
  form: BloodGlucoseForm
  patientId: string
}) => {
  const navigate = useNavigate()
  const returnUrl = useDashboardReturnUrl()
  const { mutateAsync: updateForm, isPending } = useUpdateBloodGlucoseFormMutation({
    patientId,
    formId: form.id,
  })

  const initialValues = useMemo<DefaultValues<FastingFormValues>>(
    () => ({
      bloodGlucoseInMilligramsPerDeciliter:
        observation.reading?.bloodGlucoseInMilligramsPerDeciliter,
      dateTime: form.dateTime.toString(),
      notes: form.notes ?? undefined,
    }),
    [observation, form]
  )

  return (
    <FastingFormPanel
      observation={observation}
      title="Edit fasting reading"
      isEditable={form.editable}
      isPending={isPending}
      initialValues={initialValues}
      onSubmit={async (data) => {
        try {
          await updateForm({
            formId: form.id,
            update: {
              notes: data.notes ?? null,
            },
          })
          navigate(returnUrl)
        } catch {
          toast.error("Failed to save measurement. Please try again.")
        }
      }}
    />
  )
}

const MealEditForm = ({
  observation,
  form,
  patientId,
  prescribedDiabetesMedications,
}: {
  observation: BloodGlucoseObservation
  form: BloodGlucoseForm
  patientId: string
  prescribedDiabetesMedications: PrescribedDiabetesMedication[]
}) => {
  const navigate = useNavigate()
  const returnUrl = useDashboardReturnUrl()
  const { mutateAsync: updateForm, isPending } = useUpdateBloodGlucoseFormMutation({
    patientId,
    formId: form.id,
  })

  const initialValues = useMemo<DefaultValues<MealFormValues>>(
    () => ({
      context: isMealContext(form.context) ? form.context : "breakfast",
      bloodGlucoseInMilligramsPerDeciliter:
        observation.reading?.bloodGlucoseInMilligramsPerDeciliter,
      dateTime: form.dateTime.toString(),
      mealContents: form.mealContents ?? undefined,
      carbsInGrams: form.carbsInGrams ?? undefined,
      proteinInGrams: form.proteinInGrams ?? undefined,
      notes: form.notes ?? undefined,
      medicationTaken: form.medications?.medicationTaken ?? undefined,
      medicationNote: form.medications?.note ?? undefined,
    }),
    [observation, form]
  )

  return (
    <MealFormPanel
      observation={observation}
      isEditable={form.editable}
      isPending={isPending}
      prescribedDiabetesMedications={prescribedDiabetesMedications}
      initialValues={initialValues}
      onSubmit={async (data) => {
        const medications = hasPrescribedMedicationForContext(
          prescribedDiabetesMedications,
          data.context,
        )
          ? toMedicationLog(data.medicationTaken ?? null, data.medicationNote ?? "")
          : null
        try {
          await updateForm({
            formId: form.id,
            update: {
              context: data.context,
              mealContents: data.mealContents ?? null,
              carbsInGrams: data.carbsInGrams ?? null,
              proteinInGrams: data.proteinInGrams ?? null,
              notes: data.notes ?? null,
              medications,
            },
          })
          navigate(returnUrl)
        } catch {
          toast.error("Failed to save measurement. Please try again.")
        }
      }}
    />
  )
}

// Contextualize panels: the patient just got an SMS for a SmartMeter reading
// or clicked a not-yet-contextualized reading from the dashboard. There's a
// reading but no form yet — POST to create one tied to readingId.
const FastingContextualizeForm = ({
  observation,
  patientId,
}: {
  observation: BloodGlucoseObservation
  patientId: string
}) => {
  const navigate = useNavigate()
  const returnUrl = useDashboardReturnUrl()
  const { mutateAsync: createForm, isPending } = useCreateBloodGlucoseFormMutation({ patientId })

  const initialValues = useMemo<DefaultValues<FastingFormValues>>(
    () => ({
      bloodGlucoseInMilligramsPerDeciliter:
        observation.reading?.bloodGlucoseInMilligramsPerDeciliter,
      dateTime: observation.dateTime.toString(),
      notes: undefined,
    }),
    [observation]
  )

  return (
    <FastingFormPanel
      observation={observation}
      title="Add fasting context"
      isEditable={observation.isFormEditable}
      isPending={isPending}
      initialValues={initialValues}
      onSubmit={async (data) => {
        const readingId = observation.reading?.id ?? null
        try {
          await createForm({
            patientId,
            form: {
              context: "fasting",
              // Backend XOR: dateTime must be null when readingId is set.
              dateTime: readingId ? null : data.dateTime,
              notes: data.notes ?? null,
              readingId,
              carbsInGrams: null,
              proteinInGrams: null,
              mealContents: null,
              medications: null,
              reasons: null,
            },
          })
          navigate(returnUrl)
        } catch {
          toast.error("Failed to save measurement. Please try again.")
        }
      }}
    />
  )
}

const MealContextualizeForm = ({
  observation,
  patientId,
  prescribedDiabetesMedications,
}: {
  observation: BloodGlucoseObservation
  patientId: string
  prescribedDiabetesMedications: PrescribedDiabetesMedication[]
}) => {
  const navigate = useNavigate()
  const returnUrl = useDashboardReturnUrl()
  const { mutateAsync: createForm, isPending } = useCreateBloodGlucoseFormMutation({ patientId })

  const initialValues = useMemo<DefaultValues<MealFormValues>>(
    () => ({
      context: "breakfast",
      bloodGlucoseInMilligramsPerDeciliter:
        observation.reading?.bloodGlucoseInMilligramsPerDeciliter,
      dateTime: observation.dateTime.toString(),
      mealContents: undefined,
      carbsInGrams: undefined,
      proteinInGrams: undefined,
      notes: undefined,
      medicationTaken: undefined,
      medicationNote: undefined,
    }),
    [observation]
  )

  return (
    <MealFormPanel
      observation={observation}
      isEditable={observation.isFormEditable}
      isPending={isPending}
      prescribedDiabetesMedications={prescribedDiabetesMedications}
      initialValues={initialValues}
      onSubmit={async (data) => {
        const readingId = observation.reading?.id ?? null
        const medications = hasPrescribedMedicationForContext(
          prescribedDiabetesMedications,
          data.context,
        )
          ? toMedicationLog(data.medicationTaken ?? null, data.medicationNote ?? "")
          : null
        try {
          await createForm({
            patientId,
            form: {
              context: data.context,
              // Backend XOR: dateTime must be null when readingId is set.
              dateTime: readingId ? null : data.dateTime,
              mealContents: data.mealContents ?? null,
              carbsInGrams: data.carbsInGrams ?? null,
              proteinInGrams: data.proteinInGrams ?? null,
              notes: data.notes ?? null,
              readingId,
              medications,
              reasons: null,
            },
          })
          navigate(returnUrl)
        } catch {
          toast.error("Failed to save measurement. Please try again.")
        }
      }}
    />
  )
}

const Contextualize = ({
  observation,
  patientId,
  prescribedDiabetesMedications,
}: {
  observation: BloodGlucoseObservation
  patientId: string
  prescribedDiabetesMedications: PrescribedDiabetesMedication[]
}) => {
  const [step, setStep] = useState<"choose" | "fasting" | "meal">("choose")

  if (step === "fasting") {
    return <FastingContextualizeForm observation={observation} patientId={patientId} />
  }
  if (step === "meal") {
    return (
      <MealContextualizeForm
        observation={observation}
        patientId={patientId}
        prescribedDiabetesMedications={prescribedDiabetesMedications}
      />
    )
  }

  const reading = observation.reading?.bloodGlucoseInMilligramsPerDeciliter
  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-bold text-primary-contrast">Add context</h1>
      {reading !== undefined && (
        <p className="text-base text-primary-contrast/80">
          Your reading was <span className="font-semibold">{reading} mg/dL</span>. When was it
          taken?
        </p>
      )}
      {!observation.isFormEditable && (
        <div className="rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <p className="font-semibold">Unable to edit form</p>
          <p className="mt-1">
            Your doctor has already reviewed your data. It is no longer possible to make changes.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <Button
          type="button"
          onClick={() => setStep("fasting")}
          disabled={!observation.isFormEditable}
          className="w-full py-3 text-base"
        >
          Fasting (before food)
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep("meal")}
          disabled={!observation.isFormEditable}
          className="w-full py-3 text-base"
        >
          With or after a meal
        </Button>
      </div>
    </div>
  )
}

const EditMeasurementPage = () => {
  const navigate = useNavigate()
  const { measurementId } = useParams<{ measurementId: string }>()
  const { data: self } = useSelfQuery()
  const { data: observation, isLoading: isObservationLoading } = useObservationByIdQuery({
    readingId: measurementId ?? null,
  })

  // Quick-check observations have a dedicated edit page. If a deep-link or a
  // dashboard click lands here for a quick-check observation, redirect.
  useEffect(() => {
    if (observation?.form?.context === "quick-check" && measurementId) {
      navigate(buildPath(ROUTES.QUICK_CHECK_EDIT, { measurementId }), {
        replace: true,
      })
    }
  }, [observation, measurementId, navigate])

  // Wait for both the observation AND self before rendering: child mutations
  // need a non-empty patientId in the POST/PATCH URL.
  if (isObservationLoading || !self) {
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  const patientId = self.id

  if (!observation) {
    return (
      <Page showAppbar={false}>
        <div className="px-4 py-8">
          <p className="text-primary-contrast/70">Measurement not found.</p>
        </div>
      </Page>
    )
  }

  if (!observation.form) {
    return (
      <Page showAppbar={false}>
        <Contextualize
          observation={observation}
          patientId={patientId}
          prescribedDiabetesMedications={self.prescribedDiabetesMedications}
        />
      </Page>
    )
  }

  if (observation.form.context === "quick-check") {
    // Render nothing while the redirect effect runs.
    return (
      <Page showAppbar={false}>
        <div className="flex items-center justify-center p-8">
          <Spinner />
        </div>
      </Page>
    )
  }

  return (
    <Page showAppbar={false}>
      {observation.form.context === "fasting" ? (
        <FastingEditForm observation={observation} form={observation.form} patientId={patientId} />
      ) : (
        <MealEditForm
          observation={observation}
          form={observation.form}
          patientId={patientId}
          prescribedDiabetesMedications={self.prescribedDiabetesMedications}
        />
      )}
    </Page>
  )
}

export default EditMeasurementPage
